import React, { useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';

const MENU_DATA = [
  {
    title: 'Wohnen im Alter',
    href: 'https://aboelo.de/wohnen-im-alter/',
    items: [
      { label: 'Barrierefreies Wohnen', href: 'https://aboelo.de/wohnen-im-alter/barrierefreies-wohnen/' },
      { label: 'Betreutes Wohnen', href: 'https://aboelo.de/wohnen-im-alter/betreutes-wohnen/' },
      { label: 'Hausnotruf', href: 'https://aboelo.de/wohnen-im-alter/hausnotruf-notfallknopf/' },
      { label: 'Pflege-WG', href: 'https://aboelo.de/wohnen-im-alter/pflege-wg/' },
      { label: 'Seniorenresidenz', href: 'https://aboelo.de/pflege-betreuung/seniorenwohnheim/' },
      { label: 'Mehrgenerationenhaus', href: 'https://aboelo.de/wohnen-im-alter/mehrgenerationenhaus/' },
      { label: 'Smart Home für Senioren', href: 'https://aboelo.de/wohnen-im-alter/aal-assisted-ambient-living/' },
    ]
  },
  {
    title: 'Pflege & Betreuung',
    href: 'https://aboelo.de/pflege-betreuung/',
    items: [
      { label: 'Ambulante Pflege', href: 'https://aboelo.de/pflege-betreuung/ambulante-pflege/' },
      { label: 'Tagespflege', href: 'https://aboelo.de/pflege-betreuung/tagespflege/' },
      { label: 'Intensivpflege', href: 'https://aboelo.de/pflege-betreuung/intensivpflege/' },
      { label: 'Verhinderungspflege', href: 'https://aboelo.de/pflege-betreuung/verhinderungspflege/' },
      { label: 'Pflegeberatung', href: 'https://aboelo.de/pflege-betreuung/pflegeberatung/' },
      { label: 'Pflegeantrag & Pflegegrad', href: 'https://aboelo.de/pflegeantrag/' },
      { label: 'Pflegekosten-Rechner', href: 'https://aboelo.de/pflegekosten-rechner/' },
    ]
  },
  {
    title: 'Gesundheit & Fitness',
    href: 'https://aboelo.de/gesundheit-fitness/',
    items: [
      { label: 'Hilfsmittel finden', href: 'https://aboelo.de/hilfsmittel' },
      { label: 'Fitness für Senioren App', href: 'https://aboelo.de/fitness' },
      { label: 'Digital Quiz', href: 'https://aboelo.de/quiz' },
      { label: 'Digitale Kompetenz', href: 'https://aboelo.de/digital' },
      { label: 'Sudoku spielen', href: 'https://aboelo.de/sudoku/' },
      { label: 'Vorsorge im Alter', href: 'https://aboelo.de/vorsorge-im-alter/' },
      { label: 'Sport im Alter', href: 'https://aboelo.de/sport-im-alter/' },
    ]
  },
  {
    title: 'Finanzen & Recht',
    href: 'https://aboelo.de/finanzen-recht/',
    items: [
      { label: 'Erbrecht', href: 'https://aboelo.de/finanzen-recht/erben-erbschaft-erbschaftsrecht/' },
      { label: 'Pflegerecht', href: 'https://aboelo.de/finanzen-recht/pflegerecht/' },
    ]
  }
];

interface HeaderProps {
  logoPath?: string;
}

export const AboeloGlobalHeader: React.FC<HeaderProps> = ({ logoPath = '/fitness/aboeloLogo.png' }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState<number | null>(null);

  // States for Desktop Dropdown Menus
  const [anchorEl0, setAnchorEl0] = useState<null | HTMLElement>(null);
  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const [anchorEl3, setAnchorEl3] = useState<null | HTMLElement>(null);

  const anchorElList = [anchorEl0, anchorEl1, anchorEl2, anchorEl3];
  const setAnchorElList = [setAnchorEl0, setAnchorEl1, setAnchorEl2, setAnchorEl3];

  const activeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, index: number) => {
    if (activeTimerRef.current) {
      clearTimeout(activeTimerRef.current);
      activeTimerRef.current = null;
    }
    // Close other menus first
    setAnchorElList.forEach((setAnchor, idx) => {
      if (idx !== index) setAnchor(null);
    });
    setAnchorElList[index](event.currentTarget);
  };

  const handleKeepMenuOpen = () => {
    if (activeTimerRef.current) {
      clearTimeout(activeTimerRef.current);
      activeTimerRef.current = null;
    }
  };

  const handleCloseMenu = (index: number) => {
    if (activeTimerRef.current) {
      clearTimeout(activeTimerRef.current);
    }
    activeTimerRef.current = setTimeout(() => {
      setAnchorElList[index](null);
    }, 150);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
    setActiveMobileSubmenu(null);
  };

  const toggleMobileSubmenu = (index: number) => {
    setActiveMobileSubmenu(activeMobileSubmenu === index ? null : index);
  };

  return (
    <AppBar position="static" color="default" elevation={1} sx={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 80, justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <a href="https://aboelo.de" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img
                src={logoPath}
                alt="aboelo Logo"
                style={{ height: 40, width: 'auto' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const nextNode = target.nextSibling as HTMLElement;
                  if (nextNode) nextNode.style.display = 'block';
                }}
              />
              <Box
                component="span"
                sx={{
                  display: 'none',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#1f5f5f',
                  letterSpacing: 0.5,
                }}
              >
                aboelo
              </Box>
            </a>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {MENU_DATA.map((menu, index) => {
              const isOpen = Boolean(anchorElList[index]);
              return (
                <Box key={index} onMouseEnter={handleKeepMenuOpen} onMouseLeave={() => handleCloseMenu(index)}>
                  <Button
                    id={`menu-button-${index}`}
                    aria-controls={isOpen ? `menu-dropdown-${index}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={isOpen ? 'true' : undefined}
                    onClick={(e) => handleOpenMenu(e, index)}
                    onMouseEnter={(e) => handleOpenMenu(e, index)}
                    endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />}
                    sx={{
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.925rem',
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      '&:hover': {
                        color: '#1f5f5f',
                        backgroundColor: '#f1f5f9',
                      },
                    }}
                  >
                    {menu.title}
                  </Button>
                  <Menu
                    id={`menu-dropdown-${index}`}
                    anchorEl={anchorElList[index]}
                    open={isOpen}
                    onClose={() => handleCloseMenu(index)}
                    MenuListProps={{
                      'aria-labelledby': `menu-button-${index}`,
                      onMouseEnter: handleKeepMenuOpen,
                      onMouseLeave: () => handleCloseMenu(index),
                      sx: { py: 1 },
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPaper-root': {
                        pointerEvents: 'auto',
                        minWidth: 240,
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #f1f5f9',
                        borderRadius: 2,
                        mt: 0.5,
                      },
                    }}
                  >
                    {menu.items.map((item, itemIdx) => (
                      <MenuItem
                        key={itemIdx}
                        onClick={() => handleCloseMenu(index)}
                        component="a"
                        href={item.href}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#475569',
                          py: 1.25,
                          px: 2.5,
                          '&:hover': {
                            backgroundColor: '#f0fdfa',
                            color: '#115e59',
                          },
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              );
            })}
          </Box>

          {/* Mobile Navigation Toggle */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="Toggle Navigation Menu"
              onClick={toggleMobileMenu}
              color="inherit"
              sx={{ color: '#475569' }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={toggleMobileMenu}
        sx={{
          zIndex: 999,
          '& .MuiDrawer-paper': {
            top: 80,
            maxHeight: 'calc(100vh - 80px)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <List sx={{ py: 2, px: 2 }}>
          {MENU_DATA.map((menu, index) => {
            const isSubOpen = activeMobileSubmenu === index;
            return (
              <Box key={index} sx={{ borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
                <ListItem
                  component="div"
                  onClick={() => toggleMobileSubmenu(index)}
                  sx={{
                    py: 2,
                    px: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <ListItemText
                    primary={menu.title}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: '#1e293b',
                      fontSize: '1rem',
                    }}
                  />
                  <KeyboardArrowDownIcon
                    sx={{
                      color: '#94a3b8',
                      transition: 'transform 0.2s',
                      transform: isSubOpen ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </ListItem>
                <Collapse in={isSubOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: '#f8fafc', borderRadius: 2, my: 1, py: 1 }}>
                    {menu.items.map((item, itemIdx) => (
                      <ListItem
                        key={itemIdx}
                        component="a"
                        href={item.href}
                        onClick={toggleMobileMenu}
                        sx={{
                          py: 1.5,
                          px: 4,
                          textDecoration: 'none',
                          color: '#475569',
                          fontSize: '0.875rem',
                          display: 'block',
                          '&:hover': {
                            backgroundColor: '#f0fdfa',
                            color: '#115e59',
                          },
                        }}
                      >
                        {item.label}
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Drawer>
    </AppBar>
  );
};
export default AboeloGlobalHeader;
